import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { LESSON } from '@/constants/testIds';

const TwoPointerVisualization = ({ array, step, lessonId }) => {
  if (!array || array.length === 0) return null;

  const { left, right } = step || { left: -1, right: -1 };

  const getCellStyle = (index) => {
    const baseStyle = "w-16 h-16 flex items-center justify-center border-2 rounded-md text-xl font-code relative";
    
    if (index === left && index === right) {
      return `${baseStyle} border-purple-500 bg-purple-500/20 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.5)]`;
    }
    if (index === left) {
      return `${baseStyle} border-[#FFB800] bg-[#FFB800]/10 text-[#FFB800] shadow-[0_0_15px_rgba(255,184,0,0.5)]`;
    }
    if (index === right) {
      return `${baseStyle} border-[#007AFF] bg-[#007AFF]/10 text-[#007AFF] shadow-[0_0_15px_rgba(0,122,255,0.5)]`;
    }
    
    // For remove duplicates
    if (lessonId === 'remove-duplicates') {
      if (step?.slow !== undefined && index <= step.slow) {
        return `${baseStyle} border-green-500/50 bg-green-500/10 text-green-400`;
      }
    }

    return `${baseStyle} border-white/20 bg-[#1A1A20] text-white`;
  };

  return (
    <div className="space-y-8">
      {/* Array with cells */}
      <div className="flex flex-wrap gap-3 justify-center items-center relative pb-16">
        {array.map((value, index) => (
          <motion.div
            key={index}
            data-testid={LESSON.arrayCell(index)}
            className={getCellStyle(index)}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <div className="absolute -top-5 text-[10px] text-[#71717A] font-code">
              {index}
            </div>
            <div>{value}</div>
          </motion.div>
        ))}

        {/* Left Pointer */}
        {left !== undefined && left >= 0 && left < array.length && (
          <motion.div
            data-testid={LESSON.pointer('left')}
            className="absolute flex flex-col items-center"
            initial={{ x: left * 76, y: 80 }}
            animate={{ x: left * 76, y: 80 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <ChevronDown className="w-6 h-6 text-[#FFB800]" />
            <span className="text-xs font-code font-bold text-[#FFB800] mt-1">
              {lessonId === 'remove-duplicates' ? 'SLOW' : 'LEFT'}
            </span>
          </motion.div>
        )}

        {/* Right Pointer */}
        {right !== undefined && right >= 0 && right < array.length && left !== right && (
          <motion.div
            data-testid={LESSON.pointer('right')}
            className="absolute flex flex-col items-center"
            initial={{ x: right * 76, y: 80 }}
            animate={{ x: right * 76, y: 80 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          >
            <ChevronDown className="w-6 h-6 text-[#007AFF]" />
            <span className="text-xs font-code font-bold text-[#007AFF] mt-1">
              {lessonId === 'remove-duplicates' ? 'FAST' : 'RIGHT'}
            </span>
          </motion.div>
        )}
      </div>

      {/* Metrics for container with most water */}
      {lessonId === 'container-with-most-water' && step?.calculation && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-3 gap-4 mt-6"
        >
          <div className="bg-[#0F0F12] border border-white/10 rounded-lg p-3 text-center">
            <div className="text-[#71717A] text-xs font-code mb-1">Width</div>
            <div className="text-[#FFB800] text-2xl font-code font-bold">{step.calculation.width}</div>
          </div>
          <div className="bg-[#0F0F12] border border-white/10 rounded-lg p-3 text-center">
            <div className="text-[#71717A] text-xs font-code mb-1">Height</div>
            <div className="text-[#007AFF] text-2xl font-code font-bold">{step.calculation.height}</div>
          </div>
          <div className="bg-[#0F0F12] border border-white/10 rounded-lg p-3 text-center">
            <div className="text-[#71717A] text-xs font-code mb-1">Area</div>
            <div className="text-white text-2xl font-code font-bold">{step.calculation.area}</div>
          </div>
        </motion.div>
      )}

      {/* Max Area Display */}
      {lessonId === 'container-with-most-water' && step?.maxArea !== undefined && (
        <div className="bg-gradient-to-r from-[#FFB800]/10 to-[#007AFF]/10 border border-[#FFB800]/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">Maximum Area Found</div>
          <div className="text-[#FFB800] text-3xl font-code font-bold">{step.maxArea}</div>
        </div>
      )}

      {/* Water Trapped Display */}
      {lessonId === 'trapping-rain-water' && step?.water !== undefined && (
        <div className="bg-gradient-to-r from-[#007AFF]/10 to-[#00D4FF]/10 border border-[#007AFF]/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">Water Trapped</div>
          <div className="text-[#007AFF] text-3xl font-code font-bold">{step.water} units</div>
        </div>
      )}

      {/* Unique Count for Remove Duplicates */}
      {lessonId === 'remove-duplicates' && step?.result !== undefined && (
        <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">Unique Elements</div>
          <div className="text-green-400 text-3xl font-code font-bold">{step.result}</div>
        </div>
      )}
    </div>
  );
};

export default TwoPointerVisualization;
